import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchCustomResults   // inner scrolling container (has id="predictive-search-results")
 * @property {HTMLElement} resetButton
 * @property {HTMLElement} [recentlyViewedWrapper]
 * @property {HTMLElement[]} [recentlyViewedTitle]
 * @property {HTMLElement[]} [recentlyViewedItems]
 * @extends {Component<Refs>}
 */
class PredictiveSearchCustomComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchCustomResults', 'resetButton'];

  #controller = new AbortController();
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** Convenience getters **/
  get #contentWrapper() {
    // The absolute-positioned container we show/hide
    return this.querySelector('.predictive-search-form__content-wrapper');
  }
  get #isOpen() {
    return this.dataset.open === 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    // Initial UI state
    this.dataset.open = 'false';
    this.#contentWrapper?.setAttribute('hidden', '');

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    // Events: outside click closes
    document.addEventListener('click', this.#onDocumentClick, { signal, capture: true });

    // Keep focus behavior: when clicking non-interactive area inside results, re-focus input
    this.addEventListener('click', this.#handlePanelClick, { signal });

    // Open on user intent only
    this.refs.searchInput.addEventListener('focus', () => {
      // Only open on focus if there is content to show (either text or we want empty state on focus)
      if (this.refs.searchInput.value.trim()) {
        this.open();
      }

      // If you want different open behavior (e.g., always open on focus to show the empty state), change the focus handler to:
      // this.#renderEmptyState();
      // this.open();
    }, { signal });

    // Note: actual input handling is via on:input="/search" in Liquid (debounced below)

    // No auto-open / no auto-reset on mount
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /** PUBLIC API — can be called from anywhere (e.g., header buttons) */
  open = () => {
    if (this.#isOpen) return;
    this.dataset.open = 'true';
    this.#contentWrapper?.removeAttribute('hidden');
    this.refs.closeModalButton?.removeAttribute('hidden');
    this.refs.searchInput?.setAttribute('aria-expanded', 'true');
    this.dispatchEvent(new CustomEvent('predictive-search-custom:open', { bubbles: true }));
  };

  close = () => {
    if (!this.#isOpen) return;
    this.refs.closeModalButton?.setAttribute('hidden', '');
    this.dataset.open = 'false';
    this.#contentWrapper?.setAttribute('hidden', '');
    this.refs.searchInput?.setAttribute('aria-expanded', 'false');
    // Clear active descendant for a11y when closed
    this.refs.searchInput?.removeAttribute('aria-activedescendant');
    this.dispatchEvent(new CustomEvent('predictive-search-custom:close', { bubbles: true }));
  };

  toggle = () => (this.#isOpen ? this.close() : this.open());

  /** Outside click closes */
  #onDocumentClick = (evt) => {
    if (!this.#isOpen) return;
    if (!this.contains(/** @type {Node} */ (evt.target))) {
      this.close();
    }
  };

  /**
   * Keep input focused unless the user clicks an interactive control inside.
   */
  #handlePanelClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractive =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button,a,input');

    if (!isInteractive && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /*** RESULTS LIST NAV ***/
  #isKeyboardNavigation = false;

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }
  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) item.classList.add('keyboard-focus');
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
        // update aria-activedescendant if item has id
        if (item.id) this.refs.searchInput.setAttribute('aria-activedescendant', item.id);
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /** Key handling on the input (wired via on:keydown in Liquid) */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      // Prevent native <input type="search"> clear-on-Esc behavior
      event.preventDefault();
      event.stopPropagation(); // optional, but nice to keep it local
      
      // Close panel only; do not clear text on Esc
      this.close();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        if (event.shiftKey) {
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchCustomResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /** Public reset from UI (wired via on:click="/resetSearch") */
  resetSearch = debounce((keepFocus = true) => {
    this.refs.searchInput.value = '';
    this.#hideResetButton()

    if (keepFocus) this.refs.searchInput.focus();
    this.#renderEmptyState();
    // When user explicitly clicks reset, close the panel
    this.close();
  }, 100);

  /** Input handler (wired via on:input="/search") */
  search = debounce((event) => {
    // If the input is not a text input (like pressing Esc), ignore
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
        // Input is empty → hide clear button
      this.#hideResetButton();

      // No query: show empty state only if user is interacting (focused); else keep closed
      if (document.activeElement === this.refs.searchInput) {
        this.#renderEmptyState();
        this.open();
      } else {
        this.close();
      }
      return;
    }

    this.#showResetButton();
    this.open(); // Open when there is a real query
    this.#getSearchResults(searchTerm);
  }, 200);

  /** Scroll reset */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchCustomResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) resultsInner.scrollTop = 0;

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) formContent.scrollTop = 0;
    });
  }

  /** Fetch results (unchanged) */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchCustomResults } = this.refs;
    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;
        if (abortController.signal.aborted) return;

        morph(predictiveSearchCustomResults, resultsMarkup);
        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /** Build empty state (extracted from your previous #resetSearch) */
  #renderEmptyState = async () => {
    const { predictiveSearchCustomResults } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmpty = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmpty) return;
    if (abortController.signal.aborted) return;

    // Recently viewed
    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (abortController.signal.aborted) return;

      if (recentlyViewedMarkup) {
        const doc = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const rv = doc.getElementById('predictive-search-products');
        if (rv) {
          for (const child of rv.children) {
            if (child instanceof HTMLElement) child.setAttribute('ref', 'recentlyViewedWrapper');
          }
          const target = parsedEmpty.querySelector('#predictive-search-products');
          if (target) target.prepend(...rv.children);
        }
      }
    }

    morph(predictiveSearchCustomResults, parsedEmpty);
    this.#resetScrollPositions();
  };

  /** Recently viewed markup fetch (unchanged) */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() { this.refs.resetButton.hidden = true; }
  #showResetButton() { this.refs.resetButton.hidden = false; }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = abortController;
    return abortController;
  }
}

if (!customElements.get('predictive-search-custom-component')) {
  customElements.define('predictive-search-custom-component', PredictiveSearchCustomComponent);
}